import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Eye, Camera, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Bot, ZoomIn, ZoomOut, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  visionStatus,
  analyzeShelf,
  listAnalyses,
  runPursuitNow,
  getPursuitLogs,
} from '../lib/api';

const verdictColor = {
  OK: 'bg-emerald-600',
  ADVERTENCIA: 'bg-amber-600',
  SOSPECHOSA: 'bg-orange-600',
  FRAUDE: 'bg-red-600',
};

export default function AIMonitorPage() {
  const [status, setStatus] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [running, setRunning] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, l] = await Promise.all([
        visionStatus(),
        listAnalyses({ limit: 30 }),
        getPursuitLogs({ limit: 30 }),
      ]);
      setStatus(s.data);
      setAnalyses(a.data || []);
      setLogs(l.data || []);
    } catch (e) {
      toast.error('Error cargando panel IA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAnalyze = async () => {
    if (!photoUrl) {
      toast.error('Pega una URL de foto para probar');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await analyzeShelf({ photo_url: photoUrl });
      if (res.data?.error) {
        toast.error(`Error: ${res.data.error}`);
      } else {
        toast.success(`Análisis listo — verdict: ${res.data.verdict}`);
      }
      await load();
    } catch (e) {
      toast.error('Error analizando foto');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRunPursuit = async () => {
    setRunning(true);
    try {
      await runPursuitNow();
      toast.success('Ciclo del agente ejecutado');
      await load();
    } catch (e) {
      toast.error('Error ejecutando agente');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-monitor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Vigilancia IA</h1>
          <p className="text-slate-500">Agente IA persigue cumplimiento + análisis de fotos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-ai-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
          </Button>
          <Button onClick={handleRunPursuit} disabled={running} data-testid="run-pursuit-btn"
            className="bg-violet-600 hover:bg-violet-700">
            <Bot className="w-4 h-4 mr-2" /> {running ? 'Ejecutando...' : 'Ejecutar agente'}
          </Button>
        </div>
      </div>

      {/* Status */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Eye className="w-4 h-4 text-violet-600" /> Estado Vision AI
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status ? (
            <div className="flex items-center gap-3">
              {status.configured ? (
                <Badge className="bg-emerald-600">Configurado</Badge>
              ) : (
                <Badge className="bg-red-600">No configurado</Badge>
              )}
              <span className="text-sm text-slate-600">
                Modelo: <span className="font-mono">{status.provider}/{status.model}</span>
              </span>
            </div>
          ) : <Skeleton className="h-6 w-40" />}
        </CardContent>
      </Card>

      {/* Test panel */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="w-4 h-4 text-blue-600" /> Probar análisis de foto
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>URL de la foto del anaquel</Label>
          <div className="flex gap-2">
            <Input
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              placeholder="https://..."
              data-testid="photo-url-input"
            />
            <Button onClick={handleAnalyze} disabled={analyzing} data-testid="analyze-btn"
              className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap">
              {analyzing ? 'Analizando...' : 'Analizar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="analyses">
        <TabsList>
          <TabsTrigger value="analyses" data-testid="tab-analyses">Análisis IA recientes</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs del Agente</TabsTrigger>
        </TabsList>

        <TabsContent value="analyses" className="space-y-3 mt-4">
          {loading ? (
            <Skeleton className="h-40" />
          ) : analyses.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">Aún no hay análisis.</CardContent></Card>
          ) : analyses.map((a) => (
            <Card key={a.id} data-testid={`analysis-card-${a.id}`} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {a.photo_url && (
                    <div
                      className="relative group cursor-pointer flex-shrink-0"
                      onClick={() => {
                        setSelectedAnalysis(a);
                        setZoomLevel(1);
                      }}
                      title="Clic para ampliar y hacer zoom"
                    >
                      <img
                        src={a.photo_url.replace(/^http:\/\/localhost:8001/, '')}
                        alt="shelf"
                        className="w-32 h-24 rounded object-cover bg-slate-100 border border-slate-200 transition-transform group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center text-white text-xs font-medium gap-1">
                        <Maximize2 className="w-4 h-4" />
                        <span>Zoom</span>
                      </div>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={verdictColor[a.verdict] || 'bg-slate-600'}>{a.verdict}</Badge>
                      <span className="text-xs text-slate-500">Ocupación: {a.occupancy_pct}%</span>
                      <span className="text-xs text-slate-400 ml-auto">
                        {new Date(a.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-emerald-700">Nuestros detectados ({a.own_products_detected?.length || 0}):</p>
                        <ul className="list-disc list-inside text-slate-600 text-xs">
                          {(a.own_products_detected || []).map((p, i) => (
                            <li key={p.sku_id || `${a.id}-own-${p.name}-${i}`}>{p.name} — {p.estimated_facing} caras ({Math.round((p.confidence||0)*100)}%)</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-orange-700">Competencia ({a.competitor_products_detected?.length || 0}):</p>
                        <ul className="list-disc list-inside text-slate-600 text-xs">
                          {(a.competitor_products_detected || []).slice(0,5).map((p, i) => (
                            <li key={`${a.id}-comp-${p.name}-${i}`}>{p.name} ({p.brand}) — {p.estimated_facing} caras</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {a.missing_own_products?.length > 0 && (
                      <div className="mt-2 flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                        <span className="text-xs text-amber-800">
                          Faltan nuestros: {a.missing_own_products.join(', ')}
                        </span>
                      </div>
                    )}
                    {a.issues?.length > 0 && (
                      <p className="mt-2 text-xs text-slate-500">Issues: {a.issues.join(' · ')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="logs" className="space-y-2 mt-4">
          {loading ? (
            <Skeleton className="h-40" />
          ) : logs.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">El agente aún no ha enviado mensajes.</CardContent></Card>
          ) : logs.map((l) => (
            <Card key={l.id} data-testid={`pursuit-log-${l.id}`} className="border-slate-200">
              <CardContent className="p-3 flex items-start gap-3">
                {l.escalated
                  ? <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                  : <CheckCircle2 className="w-4 h-4 text-violet-600 mt-0.5" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="outline">{l.kind}</Badge>
                    <span className="text-slate-400">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1">{l.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Modal de Inspección y Zoom para Auditoría */}
      {selectedAnalysis && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col p-4 animate-in fade-in duration-150">
          <div className="flex items-center justify-between text-white pb-3 border-b border-white/10 px-2">
            <div className="flex items-center gap-3">
              <Badge className={verdictColor[selectedAnalysis.verdict] || 'bg-slate-600'}>
                {selectedAnalysis.verdict}
              </Badge>
              <span className="font-medium text-sm">
                Inspección de Foto · {new Date(selectedAnalysis.created_at).toLocaleString()}
              </span>
              <span className="text-xs text-slate-300">
                Ocupación: {selectedAnalysis.occupancy_pct}%
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 text-white hover:bg-white/20 border-white/20 h-8 px-2 text-xs"
                onClick={() => setZoomLevel((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              >
                <ZoomOut className="w-4 h-4 mr-1" /> Zoom -
              </Button>
              <span className="text-xs font-mono px-2 text-white/90">{Math.round(zoomLevel * 100)}%</span>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 text-white hover:bg-white/20 border-white/20 h-8 px-2 text-xs"
                onClick={() => setZoomLevel((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
              >
                <ZoomIn className="w-4 h-4 mr-1" /> Zoom +
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 text-white hover:bg-white/20 border-white/20 h-8 px-2 text-xs"
                onClick={() => setZoomLevel(1)}
              >
                100%
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 h-8 w-8 p-0 ml-2"
                onClick={() => setSelectedAnalysis(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto flex items-center justify-center p-2 min-h-0">
            <img
              src={selectedAnalysis.photo_url?.replace(/^http:\/\/localhost:8001/, '')}
              alt="Percha ampliada"
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                transition: 'transform 0.15s ease-out',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
              className="rounded select-none shadow-2xl"
            />
          </div>

          {/* Panel inferior con detalle de predicción */}
          <div className="bg-slate-900/95 border border-white/10 p-3 rounded-lg text-white text-xs max-h-40 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-emerald-400 mb-1">
                  Nuestros detectados ({selectedAnalysis.own_products_detected?.length || 0}):
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                  {(selectedAnalysis.own_products_detected || []).map((p, i) => (
                    <li key={`zoom-own-${i}`}>
                      <strong className="text-white">{p.name}</strong>: {p.estimated_facing} frentes ({Math.round((p.confidence || 0) * 100)}% certeza)
                      {p.shelf_level ? ` · ${p.shelf_level}` : ''}
                      {p.notes ? ` · ${p.notes}` : ''}
                    </li>
                  ))}
                  {(!selectedAnalysis.own_products_detected || selectedAnalysis.own_products_detected.length === 0) && (
                    <li className="text-slate-400 italic">No se detectaron productos propios.</li>
                  )}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-amber-400 mb-1">
                  Quiebres / Faltantes ({selectedAnalysis.missing_own_products?.length || 0}):
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-amber-200">
                  {(selectedAnalysis.missing_own_products || []).map((m, i) => (
                    <li key={`zoom-miss-${i}`}>{m}</li>
                  ))}
                  {(!selectedAnalysis.missing_own_products || selectedAnalysis.missing_own_products.length === 0) && (
                    <li className="text-emerald-400 italic">Sin quiebres reportados.</li>
                  )}
                </ul>
                {selectedAnalysis.issues?.length > 0 && (
                  <p className="mt-2 text-rose-300 font-medium">Novedades: {selectedAnalysis.issues.join(' · ')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
